<template>
  <PageTemplate class="premium">
    <BlockContainer class="pb-5">
      <Header title="Oppgrader til Premium for kr. 199/mnd." />

      <div class="premium__section mt-5">
        <div>
          <img src="@/assets/subscription/subscription2.png" />
        </div>
        <div class="premium__section__content">
          <div>
            <span class="basic__section__content__button"> Premium </span>
          </div>
          <p>Indeksregulering av husleien</p>
          <h3>Unngå tap av leieinntekter</h3>
          <p>
            Husleien faktureres månedlig, og ved forsinket betaling purres det automatisk på SMS og e-post før eventuell
            inkasso og/eller prosess for utkastelse.
          </p>
          <div class="d-flex flex-row align-items-center">
            <a href="#tenancies" class="btn btn-sm btn-primary"> Start å kreve inn husleien nå! </a>
            <a
              href="https://www.husleie.no/utleier/indeksregulering-av-husleien/"
              class="btn btn-sm btn-outline-secondary ml-3"
              target="_blank"
            >
              Les mer
            </a>
          </div>
        </div>
      </div>

      <div class="premium__section">
        <div class="premium__section__content">
          <div>
            <span class="basic__section__content__button"> Premium </span>
          </div>
          <p>Utleieforsikring inkludert</p>
          <h3>Vær på den sikre siden!</h3>
          <p>
            Mange utleiere opplever at leietaker ikke har den samme omtanken for boligen som en selv har, og dessverre
            er det også noen leietakere som ikke overholder sin forpliktelse til å betale husleie for leieboligen. Da
            kan det lønne seg å ha en utleieforsikring kombinert med et forvaltet leieforhold.
          </p>
          <div class="d-flex flex-row align-items-center">
            <a href="#tenancies" class="btn btn-sm btn-primary"> Få utleieforsikring nå </a>
            <a
              href="https://www.husleie.no/utleier/utleieforsikring/"
              class="btn btn-sm btn-outline-secondary ml-3"
              target="_blank"
            >
              Les mer
            </a>
          </div>
        </div>
        <div>
          <img src="@/assets/subscription/subscription3.png" />
        </div>
      </div>

      <div class="premium__benefitCards">
        <Card header="Basic" amount="99" :benefits="benefitsBasic" />
        <Card header="Premium" amount="199" :benefits="benefitsPremium" />
      </div>

      <div class="premium__detailedBenefits">
        <h4 class="mb-4">Detaljert innhold i abonnementsplanene</h4>
        <Detailed :benefits="detailedBenefits" class="mt-5" />
      </div>

      <div class="premium__faq">
        <h4 class="mt-5">Ofte stilte spørsmål</h4>

        <div class="premium__faq__content mt-4">
          <Accordion v-for="(item, index) in questions" :key="index" :title="item.title" :content="item.content" />
        </div>
      </div>
    </BlockContainer>

    <BlockContainer class="premium__tenancies" id="tenancies">
      <div class="d-flex align-items-center mb-4">
        <i class="h5 budicon-email" />
        <h5 class="ml-4">Hvilke leieforhold ønsker du å oppgradere?</h5>
      </div>
      <p>
        Leieforholdene listet under er ikke registrert med forvaltningsabonnement fra Husleie.no. Klikk oppgrader for å
        velge mellom Basic (99 / mnd) eller Premium (199 / mnd). Du kan lese mer om våre abonnementer
        <a href="https://www.husleie.no/priser/">her</a>. Du kan også velge å skjule leieforholdet fra listen.
      </p>

      <Tenancies
        :tenancies="filteredTenancies"
        :shouldShowRentalObjectCheckbox="false"
        :shouldShowPaymentStatus="false"
        :shouldShowNotifications="false"
        class="mt-5"
      />
    </BlockContainer>
  </PageTemplate>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import PageTemplate from '@/components/PageTemplate.vue';
import BlockContainer from '@/components/BlockContainer.vue';
import Header from '@/components/subscription/Header.vue';
import Card from '@/components/subscription/Card.vue';
import Detailed from '@/components/subscription/Detailed.vue';
import Accordion from '@/components/subscription/Accordion.vue';
import Tenancies from '@/components/Tenancies.vue';

export default Vue.extend({
  name: 'Basic',
  components: {
    PageTemplate,
    BlockContainer,
    Header,
    Card,
    Detailed,
    Accordion,
    Tenancies,
  },
  computed: {
    ...mapGetters(['getAllTenancies']),

    filteredTenancies() {
      return this.getAllTenancies.filter(tenancy => {
        if (tenancy.status === 'shadow') {
          return false;
        }
        const product = tenancy.products.find(prod => prod.name === 'rentadm');

        if (!product || (product && product.level !== 'premium')) {
          return true;
        }
        return false;
      });
    },

    benefitsBasic() {
      return {
        title: 'Alt i Gratis, pluss:',
        items: [
          'Fakturering av husleien',
          'Betalingsoppfølging',
          'Bistand til utkastelse',
          'Depositumskonto',
          'Telefonsupport',
          'Advokat (750,- 15min)',
        ],
      };
    },
    benefitsPremium() {
      return {
        title: 'Alt i Basic, pluss:',
        items: [
          'Indeksregulering',
          'Forsikring mot ubet. leie*',
          'Boligskadeforsikring*',
          'Merutgifter utkastelse*',
          'Advokat (1t / år)',
        ],
      };
    },
    questions() {
      return [
        {
          title: 'Hvorfor bør jeg kjøpe et abonnement?',
          content:
            'Det vil gjøre utleieopplevelsen din enklere, tryggere og mer oversiktlig. Og om du skulle møte på noen utfordringer, så har du oss i ryggen.',
        },
        {
          title: 'Må jeg betale per leieforhold?',
          content:
            'Du får tilsendt faktura på din registrerte epost-adresse og vi fakturerer kvartalsvis. Om du ikke har mottatt faktura bør du sjekke spam-filteret. Fakturagrunnlaget ligger tilgjengelig på MinHusleie.',
        },
        {
          title: 'Må jeg legge inn et betalingskort?',
          content: 'Nei, vi sender deg faktura på e-post',
        },
        {
          title: 'Kan jeg kun kjøpe utleieforsikring?',
          content:
            'Utleieforsikring er inkludert i totalpakken du får ved et premium-abonnement. Denne kan ikke kjøpes som et eget produkt.',
        },
        {
          title: 'Tar det lang tid før jeg får utbetalt husleien fra Husleie.no?',
          content: 'Vi betaler ut husleien samme virkedag som vi mottar den på vår klientkonto.',
        },
      ];
    },
    detailedBenefits() {
      return [
        {
          benefit: 'Leiekontrakt m/ BankID',
          active: [true, true, true],
        },
        {
          benefit: 'Min side',
          active: [true, true, true],
        },
        {
          benefit: 'Overtakelsesprotokoll',
          active: [true, true, true],
        },
        {
          benefit: 'Annonsering til FINN.no ***',
          active: [true, true, true],
        },
        {
          benefit: 'Oppsigelse og heving',
          active: [true, true, true],
        },
        {
          benefit: 'Juridisk bistand',
          active: [false, '750 kr/15 min', '1t/år'],
        },
        {
          benefit: 'SMS-utsendelse',
          active: [false, true, true],
        },
        {
          benefit: 'Kundeservice',
          active: [false, true, true],
        },
        {
          benefit: 'Fakturering av husleien (auto)',
          active: [false, true, true],
        },
        {
          benefit: 'Purring og inkasso (auto)',
          active: [false, true, true],
        },
        {
          benefit: 'Depositumskonto*',
          active: ['Kr 599,-', true, true],
        },
        {
          benefit: 'Indeksregulering',
          active: [false, false, true],
        },
        {
          benefit: 'Boligskade opp til kr 500 000 *',
          active: [false, false, true],
        },
        {
          benefit: 'Tapt husleie opp til 6 mnd **',
          active: [false, false, true],
        },
        {
          benefit: 'Merutgifter utkastelse **',
          active: [false, false, true],
        },
      ];
    },
  },
  methods: {
    ...mapActions(['fetchData']),
  },
  created() {
    this.fetchData({
      tenancies: ['active', 'free'],
    });
  },
});
</script>

<style lang="scss" scoped>
.premium {
  max-width: 1920px;
}
.premium__section {
  display: grid;
  grid-template-columns: 1fr;
  background: #f3fbf7 !important;
  padding: 2rem;
  margin-bottom: 2rem;
}
.premium__section__content {
  display: flex;
  flex-direction: column;
  gap: 1rem 0rem;
}
.basic__section__content__button {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  line-height: 1.5;
  border-radius: 0.2rem;
  color: grey;
  border: 1px solid grey;
}
.premium__section img {
  display: none;
}
.premium__benefitCards {
  display: flex;
  flex-direction: column;
  gap: 2rem 0rem;
}
.premium__benefitCards > :last-child {
  border-top: 30px solid $light;
}
.premium__detailedBenefits {
  display: none;
}
.premium__faq {
  display: none;
}
.premium__tenancies {
  margin-top: 1.8rem;
  padding: 2rem;
}

@media (min-width: 500px) {
  .premium__section {
    display: grid;
    gap: 0rem 2rem;
    grid-template-columns: 1fr 1.2fr;
    padding: 0;
    background: #f3fbf7 !important;
    margin-bottom: 8rem;
  }
  .premium__section__content {
    display: flex;
    flex-direction: column;
    padding: 4rem;
    gap: 1rem 0rem;
  }
  .premium__section img {
    display: block;
    width: 100%;
  }
  .premium__benefitCards {
    display: flex;
    flex-direction: row;
    justify-content: center;
    gap: 0rem 1rem;
  }
  .premium__benefitCards > :last-child {
    border: 0;
  }
  .premium__detailedBenefits {
    display: flex;
    flex-direction: column;
    border-top: 0;
    padding: 0rem 14rem;
    margin-top: 8rem;
  }
  .premium__faq {
    display: flex;
    flex-direction: column;
    border-top: 0;
    padding: 0rem 14rem;
    margin-top: 1rem;
    gap: 1rem 0rem;
  }
  .premium__faq__content {
    display: flex;
    flex-direction: column;
    gap: 1rem 0rem;
  }
  .premium__tenancies {
    margin-top: 3rem;
    padding: 2rem;
  }
}
</style>
